import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { AutoComplete } from 'antd';
import { injectIntl } from 'react-intl';
import styled from 'styled-components';
import { sanitize } from 'utils/helpers';
import debounce from 'lodash.debounce';
import { useDispatch, useSelector } from 'react-redux';
import { Interweave } from 'interweave';
import { searchValueByType } from '../containers/search-actions';

const { Option } = AutoComplete;

const Item = styled.span`
  font-size: 16px;
  > .highlight {
    font-weight: 700;
    color: ${p => p.theme.colors.theme};
  }
`;

const EmailDetail = styled.span`
  font-size: 12px;
  font-style: italic;
  float:right;
  > .highlight {
    font-weight: 700;
    color: ${p => p.theme.colors.theme};
  }
`;

const fetchData = (v, dispatch, type, filters) => {
  dispatch(searchValueByType({
    type,
    searchValue: v,
    filters,
  }));
};

const SearchInput = ({
  intl,
  disabled,
  handleChange,
  allowClear = false,
  type,
  filters = {},
  handleSelect,
  onClear,
  ...rest
}) => {
  const options = useSelector(state => state.search.searchValuesByType);
  const isLoading = useSelector(state => state.search.isSearching);
  const dispatch = useDispatch();
  // https://joaoforja.com/blog/a-common-misconception-with-use-memo-and-use-callback/
  const debouncedSearch = useRef(
    debounce(v => fetchData(v, dispatch, type, filters), 300),
  ).current;

  const onSearch = (e) => {
    if (!e && onClear) {
      onClear();
    }
    if (e.trim()) {
      debouncedSearch(e);
    }
  };

  const handleSearchSelect = (e, option) => {
    if (handleSelect) {
      handleSelect(option.key, option);
    }
  };

  return (
    <AutoComplete
      allowClear={allowClear}
      onSearch={onSearch}
      disabled={disabled}
      onChange={handleChange}
      onSelect={handleSearchSelect}
      loading={isLoading}
      notFoundContent={null}
      filterOption={false}
      defaultActiveFirstOption={false}
      dropdownMatchSelectWidth={false}
      placeholder={intl.formatMessage({
        id: 'order.subcontracts.START_SEARCHING',
      })}
      onClear={onClear}
      {...rest}
    >
      {options?.map(v =>
        <Option
          key={v.number || v.id}
          value={sanitize(v.line1)}
          label={sanitize(v.line1)}
        >
          <Item>
            <Interweave content={v.line1} noWrap />
          </Item>
          {v.line4 && <EmailDetail>{v.line4}</EmailDetail>}
        </Option>,
      )}
    </AutoComplete>
  );
};

SearchInput.propTypes = {
  intl: PropTypes.object,
  disabled: PropTypes.bool,
  handleChange: PropTypes.func,
  handleSelect: PropTypes.func,
  allowClear: PropTypes.bool,
  type: PropTypes.string,
  filters: PropTypes.object,
  onClear: PropTypes.func,
};

export default injectIntl(SearchInput);
